import React, { useState } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import { uploadBeforeAfterImage } from '../../services/api';

const AddBeforeAfterImageModal = ({ isOpen, onClose, onSuccess, clinicServiceId }) => {
    const [formData, setFormData] = useState({
        description: '',
        beforeServiceDate: '',
        afterServiceDate: '',
        patientConsent: false
    });
    const [beforeImage, setBeforeImage] = useState(null);
    const [afterImage, setAfterImage] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        if (!beforeImage) {
            newErrors.beforeImage = 'Before image is required';
        }

        if (!afterImage) {
            newErrors.afterImage = 'After image is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        } else if (formData.description.trim().length < 5) {
            newErrors.description = 'Description must be at least 5 characters';
        }

        if (!formData.beforeServiceDate) {
            newErrors.beforeServiceDate = 'Before service date is required';
        }

        if (!formData.afterServiceDate) {
            newErrors.afterServiceDate = 'After service date is required';
        }

        if (formData.beforeServiceDate && formData.afterServiceDate) {
            const beforeDate = new Date(formData.beforeServiceDate);
            const afterDate = new Date(formData.afterServiceDate);
            if (beforeDate >= afterDate) {
                newErrors.afterServiceDate = 'After date must be after before date';
            }
        }

        if (!formData.patientConsent) {
            newErrors.patientConsent = 'Patient consent is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleImageChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setErrors(prev => ({
                    ...prev,
                    [type]: 'Please select a valid image file'
                }));
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({
                    ...prev,
                    [type]: 'Image size must be less than 5MB'
                }));
                return;
            }

            if (type === 'beforeImage') {
                setBeforeImage(file);
            } else {
                setAfterImage(file);
            }

            // Clear error
            if (errors[type]) {
                setErrors(prev => ({
                    ...prev,
                    [type]: ''
                }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const uploadFormData = new FormData();
            uploadFormData.append('clinicServiceId', clinicServiceId.toString());
            uploadFormData.append('beforeImage', beforeImage);
            uploadFormData.append('afterImage', afterImage);
            uploadFormData.append('description', formData.description.trim());
            uploadFormData.append('beforeServiceDate', formData.beforeServiceDate);
            uploadFormData.append('afterServiceDate', formData.afterServiceDate);
            uploadFormData.append('patientConsent', formData.patientConsent.toString());

            const response = await uploadBeforeAfterImage(uploadFormData);
            
            if (response.success) {
                onSuccess(response.data);
                handleClose();
            } else {
                setErrors({ submit: response.message || 'Failed to upload images' });
            }
        } catch (error) {
            setErrors({ submit: 'An unexpected error occurred' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            description: '',
            beforeServiceDate: '',
            afterServiceDate: '',
            patientConsent: false
        });
        setBeforeImage(null);
        setAfterImage(null);
        setErrors({});
        setLoading(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Add Before/After Images</h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                    {/* Before Image */}
                    <div>
                        <label htmlFor="beforeImage" className="block text-sm font-medium text-gray-700 mb-1">
                            Before Image *
                        </label>
                        <input
                            type="file"
                            id="beforeImage"
                            accept="image/*"
                            onChange={(e) => handleImageChange(e, 'beforeImage')}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.beforeImage ? 'border-red-500' : 'border-gray-300'
                            }`}
                            required
                        />
                        {errors.beforeImage && (
                            <p className="text-sm text-red-600 mt-1">{errors.beforeImage}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Select the image before treatment (max 5MB)
                        </p>
                    </div>

                    {/* After Image */}
                    <div>
                        <label htmlFor="afterImage" className="block text-sm font-medium text-gray-700 mb-1">
                            After Image *
                        </label>
                        <input
                            type="file"
                            id="afterImage"
                            accept="image/*"
                            onChange={(e) => handleImageChange(e, 'afterImage')}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.afterImage ? 'border-red-500' : 'border-gray-300'
                            }`}
                            required
                        />
                        {errors.afterImage && (
                            <p className="text-sm text-red-600 mt-1">{errors.afterImage}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Select the image after treatment (max 5MB)
                        </p>
                    </div>

                    {/* Description */}
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Describe the transformation or treatment details..."
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.description ? 'border-red-500' : 'border-gray-300'
                            }`}
                            required
                        />
                        {errors.description && (
                            <p className="text-sm text-red-600 mt-1">{errors.description}</p>
                        )}
                    </div>

                    {/* Before Service Date */}
                    <div>
                        <label htmlFor="beforeServiceDate" className="block text-sm font-medium text-gray-700 mb-1">
                            Before Service Date *
                        </label>
                        <Input
                            type="date"
                            id="beforeServiceDate"
                            name="beforeServiceDate"
                            value={formData.beforeServiceDate}
                            onChange={handleInputChange}
                            error={errors.beforeServiceDate}
                            required
                        />
                    </div>

                    {/* After Service Date */}
                    <div>
                        <label htmlFor="afterServiceDate" className="block text-sm font-medium text-gray-700 mb-1">
                            After Service Date *
                        </label>
                        <Input
                            type="date"
                            id="afterServiceDate"
                            name="afterServiceDate"
                            value={formData.afterServiceDate}
                            onChange={handleInputChange}
                            error={errors.afterServiceDate}
                            required
                        />
                    </div>

                    {/* Patient Consent */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="patientConsent"
                            name="patientConsent"
                            checked={formData.patientConsent}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            required
                        />
                        <label htmlFor="patientConsent" className="ml-2 block text-sm text-gray-900">
                            Patient has given consent to use these images *
                        </label>
                    </div>
                    {errors.patientConsent && (
                        <p className="text-sm text-red-600 mt-1">{errors.patientConsent}</p>
                    )}
                    <p className="text-xs text-gray-500">
                        You must have patient consent to upload before/after images
                    </p>

                    {/* Submit Error */}
                    {errors.submit && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                            <p className="text-sm text-red-600">{errors.submit}</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            className="flex-1"
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            className="flex-1"
                            loading={loading}
                        >
                            Upload Images
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddBeforeAfterImageModal; 